import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CommunicationPanel = ({ student }) => {
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('messages'); // messages, appointments, notifications

  const mockMessages = [
    {
      id: 1,
      sender: 'parent',
      content: 'Comment se passe ta préparation pour le quiz de sciences ?',
      timestamp: '2026-02-11T10:30:00',
      read: true
    },
    {
      id: 2,
      sender: 'student',
      content: 'Ça va bien ! J\'ai complété le quiz hier et j\'ai obtenu 85%.',
      timestamp: '2026-02-11T11:15:00',
      read: true
    },
    {
      id: 3,
      sender: 'parent',
      content: 'Excellent ! As-tu consulté les nouvelles recommandations de filières ?',
      timestamp: '2026-02-11T14:20:00',
      read: true
    },
    {
      id: 4,
      sender: 'student',
      content: 'Oui, Sciences de l\'Ingénieur m\'intéresse beaucoup !',
      timestamp: '2026-02-11T15:45:00',
      read: false
    }
  ];

  const mockAppointments = [
    {
      id: 1,
      title: 'Session d\'Orientation',
      date: '2026-02-15T14:00:00',
      duration: '1 heure',
      type: 'video',
      status: 'confirmed'
    },
    {
      id: 2,
      title: 'Révision des Résultats',
      date: '2026-02-20T16:00:00',
      duration: '45 minutes',
      type: 'in-person',
      status: 'pending'
    }
  ];

  const mockNotifications = [
    {
      id: 1,
      type: 'quiz',
      title: 'Quiz Complété',
      message: `${student?.name} a complété le quiz "Sciences de l'Ingénieur" avec un score de 85%`,
      timestamp: '2026-02-11T09:30:00',
      read: true
    },
    {
      id: 2,
      type: 'achievement',
      title: 'Nouveau Badge',
      message: `${student?.name} a débloqué le badge "Explorateur Scientifique"`,
      timestamp: '2026-02-10T18:20:00',
      read: true
    },
    {
      id: 3,
      type: 'recommendation',
      title: 'Nouvelle Recommandation',
      message: 'Une nouvelle filière a été recommandée basée sur les résultats récents',
      timestamp: '2026-02-09T12:15:00',
      read: false
    }
  ];

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleSendMessage = () => {
    if (message?.trim()) {
      console.log('Envoi du message:', message);
      setMessage('');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      quiz: 'ClipboardCheck',
      achievement: 'Award',
      recommendation: 'Lightbulb',
      system: 'Bell'
    };
    return icons?.[type] || 'Bell';
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="MessageSquare" size={24} className="text-primary" />
        <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
          Communication avec {student?.name}
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'messages' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="MessageCircle" size={16} className="inline mr-2" />
          Messages
        </button>
        <button
          onClick={() => setActiveTab('appointments')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'appointments' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Calendar" size={16} className="inline mr-2" />
          Rendez-vous
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'notifications'
              ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Bell" size={16} className="inline mr-2" />
          Notifications
        </button>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div>
          <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
            {mockMessages?.map((msg) => (
              <div
                key={msg?.id}
                className={`flex ${
                  msg?.sender === 'parent' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg?.sender === 'parent' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm mb-1">{msg?.content}</p>
                  <p className="text-xs opacity-70">{formatTime(msg?.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder="Écrivez votre message..."
              onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
            />
            <Button iconName="Send" onClick={handleSendMessage}>
              Envoyer
            </Button>
          </div>
        </div>
      )}

      {/* Appointments Tab */}
      {activeTab === 'appointments' && (
        <div className="space-y-4">
          {mockAppointments?.map((apt) => (
            <div
              key={apt?.id}
              className="border border-border rounded-lg p-4 hover:shadow-md transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={apt?.type === 'video' ? 'Video' : 'Users'}
                      size={20}
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      {apt?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(apt?.date)} à {formatTime(apt?.date)}
                    </p>
                    <p className="text-sm text-muted-foreground">Durée: {apt?.duration}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    apt?.status === 'confirmed' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}
                >
                  {apt?.status === 'confirmed' ? 'Confirmé' : 'En attente'}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" iconName="Edit">
                  Modifier
                </Button>
                <Button size="sm" variant="outline" iconName="X">
                  Annuler
                </Button>
              </div>
            </div>
          ))}
          <Button fullWidth iconName="Plus">
            Planifier un Nouveau Rendez-vous
          </Button>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-3">
          {mockNotifications?.map((notif) => (
            <div
              key={notif?.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                notif?.read ? 'border-border bg-card' : 'border-primary/50 bg-primary/5'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={getNotificationIcon(notif?.type)} size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground mb-1">{notif?.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{notif?.message}</p>
                <p className="text-xs text-muted-foreground">{formatTime(notif?.timestamp)}</p>
              </div>
              {!notif?.read && (
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunicationPanel;